import { useState, useEffect } from 'react';
import { LayoutDashboard, Settings, Save } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface UpsellFeature {
  key: string;
  enabled: boolean;
  name: string;
  description: string;
  configPath?: string;
}

interface UpsellConfig {
  associations: { enabled: boolean };
  volume_discounts: { enabled: boolean };
  bundles: { enabled: boolean };
  timers: { enabled: boolean };
  email_automation: { enabled: boolean };
  loyalty: { enabled: boolean };
  referrals: { enabled: boolean };
  gift_cards: { enabled: boolean };
}

const FEATURES: Array<Omit<UpsellFeature, 'enabled'>> = [
  {
    key: 'associations',
    name: 'Product Associations',
    description: 'Show related products and cross-sell suggestions',
    configPath: '/admin/upsell/associations',
  },
  {
    key: 'volume_discounts',
    name: 'Volume Discounts',
    description: 'Bulk purchase discounts (buy more, save more)',
    configPath: '/admin/upsell/volume-discounts',
  },
  {
    key: 'bundles',
    name: 'Product Bundles',
    description: 'Curated product bundles with discounted pricing',
    configPath: '/admin/upsell/bundles',
  },
  {
    key: 'timers',
    name: 'Countdown Timers',
    description: 'Urgency timers for limited-time offers',
    configPath: '/admin/upsell/timers',
  },
  {
    key: 'email_automation',
    name: 'Email Automation',
    description: 'Abandoned cart, review requests, and back-in-stock alerts',
    configPath: '/admin/upsell/email-automation',
  },
  {
    key: 'loyalty',
    name: 'Loyalty Program',
    description: 'Points-based rewards for repeat customers',
    configPath: '/admin/upsell/loyalty',
  },
  {
    key: 'referrals',
    name: 'Referral Program',
    description: 'Customer referral incentives and tracking',
    configPath: '/admin/upsell/referrals',
  },
  {
    key: 'gift_cards',
    name: 'Gift Cards',
    description: 'Digital gift card sales and redemption',
    configPath: '/admin/upsell/gift-cards',
  },
];

export default function UpsellDashboard() {
  const [config, setConfig] = useState<UpsellConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  async function fetchConfig() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{ settings: Record<string, any> }>('/admin/settings');
      const saved = response.settings.upsell_config || {};

      // Merge with defaults
      const merged: UpsellConfig = {
        associations: { enabled: false, ...saved.associations },
        volume_discounts: { enabled: false, ...saved.volume_discounts },
        bundles: { enabled: false, ...saved.bundles },
        timers: { enabled: false, ...saved.timers },
        email_automation: { enabled: false, ...saved.email_automation },
        loyalty: { enabled: false, ...saved.loyalty },
        referrals: { enabled: false, ...saved.referrals },
        gift_cards: { enabled: false, ...saved.gift_cards },
      };

      setConfig(merged);
    } catch (err) {
      setError('Failed to load upsell settings. Please try again.');
      console.error('Error fetching upsell config:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSave() {
    if (!config) return;

    setSuccessMessage(null);

    try {
      setIsSaving(true);
      setError(null);
      await api.put('/admin/settings', {
        settings: { upsell_config: config },
      });
      setSuccessMessage('Upsell settings saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to save settings. Please try again.');
      console.error('Error saving upsell config:', err);
    } finally {
      setIsSaving(false);
    }
  }

  function toggleFeature(key: keyof UpsellConfig) {
    if (!config) return;
    setConfig({
      ...config,
      [key]: { ...config[key], enabled: !config[key].enabled },
    });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading upsell dashboard...</span>
      </div>
    );
  }

  if (!config) {
    return null;
  }

  const features: UpsellFeature[] = FEATURES.map(f => ({
    ...f,
    enabled: config[f.key as keyof UpsellConfig]?.enabled || false,
  }));

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <LayoutDashboard className="w-8 h-8 text-gray-700" aria-hidden="true" />
          <h1 className="text-3xl font-bold text-gray-900">Upsell & Conversion Dashboard</h1>
        </div>
        <p className="text-gray-600">Enable and configure upsell features to increase revenue</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" role="status" aria-live="polite">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {features.map(feature => (
          <Card key={feature.key} padding="lg">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-stone-900 mb-1">{feature.name}</h3>
                <p className="text-sm text-stone-600">{feature.description}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={feature.enabled}
                  onChange={() => toggleFeature(feature.key as keyof UpsellConfig)}
                />
                <div className="w-11 h-6 bg-stone-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-stone-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
              </label>
            </div>
            {feature.configPath && (
              <Link
                to={feature.configPath}
                className="inline-flex items-center text-sm font-medium text-green-700 hover:text-green-800"
              >
                <Settings className="w-4 h-4 mr-1" aria-hidden="true" />
                Configure
              </Link>
            )}
          </Card>
        ))}
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          variant="primary"
          size="lg"
          isLoading={isSaving}
          onClick={handleSave}
        >
          <Save className="w-5 h-5 mr-2" aria-hidden="true" />
          Save All Settings
        </Button>
      </div>
    </div>
  );
}
